import { Component, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { Gtag } from 'angular-gtag';
import { MessageService } from 'primeng/api';
import { Observable, Subscription, map } from 'rxjs';
import { EXIT_ORDER_MESSAGES } from 'src/app/core/constants';
import { SetActiveStepConfirmation } from 'src/app/core/store/order-wizard/order-wizard-navigation/order-wizard-navigation.actions';
import { OrderWizardDataState } from 'src/app/core/store/order-wizard/order-wizard-data/order-wizard-data.state';
import { OrderWizardNavigationState } from 'src/app/core/store/order-wizard/order-wizard-navigation/order-wizard-navigation.state';
import { WizardStep } from 'src/app/core/store/order-wizard/order-wizard-navigation/order-wizard-navigation.model';
import { ExitOrderWizardStatus } from 'src/app/core/store/order-wizard/order-wizard-data/order-wizard-data.model';

@Component({
  selector: 'app-wizard-footer',
  templateUrl: './wizard-footer.component.html',
  styleUrls: ['./wizard-footer.component.scss'],
})
export class WizardFooterComponent {
  @Input() submitForm: () => boolean = () => true;
  @Input() disableNav?: boolean = false;
  @Input() nextLabel?: string | null;

  @Select(OrderWizardNavigationState.activeStep) activeStep$!: Observable<WizardStep>;
  @Select(OrderWizardNavigationState.previousStep) previousStep$!: Observable<WizardStep | null>;
  @Select(OrderWizardNavigationState.nextStep) nextStep$!: Observable<WizardStep | null>;
  @Select(OrderWizardDataState.exitOrderWizard) exitOrderWizard$!: Observable<ExitOrderWizardStatus>;

  defaultPreviousStep = 'Back';
  defaultNextStep = 'Next';
  displayExitModal: boolean = false;
  nextStepSubscription: Subscription | null = null;
  exitMessage$ = this.exitOrderWizard$.pipe(map(({ messageKey }) => (EXIT_ORDER_MESSAGES as any)[messageKey]));

  get isMakePaymentPage(): boolean {
    return this.router.url?.includes('make-payment') || false;
  }

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService,
    private gtag: Gtag
  ) {}

  onPrevious() {
    const exitOrderWizard = this.store.selectSnapshot(OrderWizardDataState.exitOrderWizard);

    if (exitOrderWizard.value === true) {
      this.showExitModal();
      return;
    }

    const previousStep = this.store.selectSnapshot(OrderWizardNavigationState.previousStep);

    if (previousStep) {
      this.router.navigate([previousStep.url], { relativeTo: this.route.parent });
    }
  }

  handleNavToNextStep() {
    const exitOrderWizard = this.store.selectSnapshot(OrderWizardDataState.exitOrderWizard);

    if (exitOrderWizard.value === true) {
      this.showExitModal();
      return;
    }

    const nextStep = this.store.selectSnapshot(OrderWizardNavigationState.nextStep);

    if (nextStep) {
      this.store.dispatch(new SetActiveStepConfirmation(true));
      this.router.navigate([nextStep.url], { relativeTo: this.route.parent });
    }
  }

  onNext() {
    const exitOrderWizard = this.store.selectSnapshot(OrderWizardDataState.exitOrderWizard);
    const isSuccessful = this.submitForm();

    if (!isSuccessful) {
      return;
    }

    if (exitOrderWizard.isUpdating) {
      this.nextStepSubscription = this.exitOrderWizard$.subscribe((s) => {
        if (!s.isUpdating) {
          if (this.nextStepSubscription) {
            this.nextStepSubscription!.unsubscribe();
          }
          this.handleNavToNextStep();
        }
      });
      return;
    }

    this.handleNavToNextStep();
  }

  showExitModal() {
    this.displayExitModal = true;
    this.gtag.event('order_wizard_exit_modal_shown', {
      event_category: 'engagement',
      non_interaction: true,
    });
  }

  hideExitModal() {
    this.displayExitModal = false;
  }

  onCancelExit() {
    this.hideExitModal();
    this.gtag.event('order_wizard_dismiss_exit_modal', {
      event_category: 'engagement',
    });
  }

  onConfirmExit() {
    this.hideExitModal();
    this.router.navigate(['/']);
    this.messageService.add({
      key: 'homeToast',
      severity: 'info',
      detail: 'Your order could not be authorized at this time.',
    });
    this.gtag.event('order_wizard_exit_via_exit_modal', {
      event_category: 'engagement',
    });
  }
}
